import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getSEOData } from '../utils/seoData';
import { CLINICAL_DISCLAIMER } from '../data/ewm';
import './NanoLM.css';
import './Narrative.css';

// ── Constants ──────────────────────────────────────────────────────────
const GATEWAY_PROXY = '/api/nanolm';

const IMAGE_EXTS = [
  '.png', '.jpg', '.jpeg', '.tif', '.tiff', '.bmp',
  '.webp', '.jfif', '.gif', '.avif', '.heic', '.heif',
];

const KNEE_KEYWORDS = ['knee', 'mri', 'meniscus', 'acl', 'ligament'];

// Model modes offered by the ewm-ui chat (see ewm-ui/main.py: ui.select([...])).
const MODEL_MODES = ['NLP', 'OCR', 'Diagnose', 'JEPA'];

const WELCOME_TEXT =
  'Welcome to Guru Emerging World Model!\n' +
  "I'm NanoLM, your AI assistant for NLP, OCR, and clinical analysis. " +
  'Ask a question or attach a file to get started.';

function fmtGB(n) {
  return (n / 1024 ** 3).toFixed(2);
}

function MemoryIcon({ size = 18 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
      <path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" />
    </svg>
  );
}

function PaperclipIcon({ size = 18 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function SendIcon({ size = 18 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

// ── Helpers (mirror ewm-ui/main.py exactly) ────────────────────────────

function nowTime() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function uuid() {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID().replace(/-/g, '');
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function _isDiagnosticRequest(text) {
  const s = (text || '').toLowerCase();
  const negation = /\b(without|no|not|dont|don|skip)\b/;
  for (const kw of ['diagnos', 'analy']) {
    let idx = s.indexOf(kw);
    while (idx !== -1) {
      const before = s.slice(Math.max(0, idx - 30), idx);
      if (!negation.test(before)) return true;
      idx = s.indexOf(kw, idx + 1);
    }
  }
  return false;
}

function _isNegatedDiagnosticRequest(text) {
  const s = (text || '').toLowerCase();
  const negation = /\b(without|no|not|dont|don|skip)\b/;
  let found = false;
  for (const kw of ['diagnos', 'analy']) {
    let idx = s.indexOf(kw);
    while (idx !== -1) {
      found = true;
      const before = s.slice(Math.max(0, idx - 30), idx);
      if (!negation.test(before)) return false;
      idx = s.indexOf(kw, idx + 1);
    }
  }
  return found;
}

function _reportToText(report) {
  if (report == null) return String(report);
  if (typeof report !== 'object') return String(report);
  if (report.error) return `Error: ${report.error}`;

  const lines = [`${report.modality || ''} report · engine ${report.engine || ''}`];

  const measurements = report.measurements || {};
  const keys = Object.keys(measurements);
  if (keys.length) {
    lines.push('');
    lines.push('Measurements:');
    keys.forEach((k) => lines.push(`  • ${k}: ${measurements[k]}`));
  }

  const findings = report.findings || [];
  if (findings.length) {
    lines.push('');
    lines.push('Findings:');
    findings.forEach((f) => lines.push(`  • ${f.signal} [${f.severity}] — ${f.note}`));
  }

  if (report.assessment) {
    lines.push('');
    lines.push(`Assessment: ${report.assessment}`);
  }
  if (report.recommendation) {
    lines.push(`Recommendation: ${report.recommendation}`);
  }
  return lines.join('\n');
}

// ── JEPA (visual memory) helpers — mirror ewm-ui/main.py ───────────────

function _jepaAction(q) {
  const s = (q || '').toLowerCase();
  if (/\bsearch\b/.test(s)) return 'search';
  if (/\b(drn|departed|retained|novel)\b/.test(s)) return 'drn';
  if (/\b(ground|grounding|hallucin)\b/.test(s)) return 'grounding';
  if (/\b(recall|holograph)\b/.test(s)) return 'holographic';
  if (/\b(match|similar|find|query|compare)\b/.test(s)) return 'query';
  return 'ingest';
}

function _jepaIngestToText(data) {
  if (data == null) return String(data);
  if (typeof data !== 'object') return String(data);
  if (data.error) return `Error: ${data.error}`;
  const mem = data.memory || {};
  const name = data.filename || 'clip';
  return [
    `✅ Saved “${name}” to my visual memory.`,
    '',
    `I now remember ${mem.memories != null ? mem.memories : '?'} clip(s) in total.`,
    '',
    'Tip: attach the same file and type “match” to find it again, or “search” to find similar clips.',
  ].join('\n');
}

function _jepaQueryToText(data) {
  if (data == null) return String(data);
  if (typeof data !== 'object') return String(data);
  if (data.error) return `Error: ${data.error}`;
  const matches = data.matches || [];
  if (!matches.length) {
    return 'No matching clips in memory yet. Ingest a clip first, or try a different file.';
  }
  const lines = [`Found ${matches.length} match${matches.length !== 1 ? 'es' : ''}:`];
  matches.forEach((m) => {
    const name = m.id || m.content_key || 'clip';
    const bss = parseFloat(m.bss) || 0;
    const jacc = parseFloat(m.jaccard) || 0;
    let verdict;
    if (bss >= 0.999 && jacc >= 0.999) verdict = 'exact match';
    else if (jacc >= 0.75) verdict = 'very similar';
    else if (jacc >= 0.4) verdict = 'similar';
    else verdict = 'weak match';
    lines.push(`  • ${name} — ${verdict} (${Math.round(jacc * 100)}% similar)`);
  });
  return lines.join('\n');
}

function _jepaSearchToText(data) {
  if (data == null) return String(data);
  if (typeof data !== 'object') return String(data);
  if (data.error) return `Error: ${data.error}`;
  const hits = data.hits || [];
  if (!hits.length) return 'Nothing similar in memory yet. Ingest a few clips first.';
  const lines = ['🔎 Similar clips I found:'];
  hits.forEach((h) => {
    const pct = Math.round((parseFloat(h.tau) || 0) * 100);
    lines.push(`  • ${h.page_id || 'clip'} — ${pct}% similar`);
  });
  return lines.join('\n');
}

function _jepaDrnToText(data) {
  if (data == null) return String(data);
  if (typeof data !== 'object') return String(data);
  if (data.error) return `Error: ${data.error}`;
  const d = data.drn || {};
  const novel = d.n_size || 0;
  const retained = d.r_size || 0;
  const lines = ['🆕 Novelty check:'];
  if (novel === 0) {
    lines.push("  I've already seen all of this — nothing new.");
  } else {
    lines.push(`  ${novel} pattern(s) are new to me.`);
  }
  lines.push(`  ${retained} pattern(s) I already knew.`);
  return lines.join('\n');
}

function _jepaGroundingToText(data) {
  if (data == null) return String(data);
  if (typeof data !== 'object') return String(data);
  if (data.error) return `Error: ${data.error}`;
  const g = data.grounding || {};
  if (g.grounded) return '✅ Grounded — this clip matches my memory.';
  return (
    "⚠️ Not grounded — this clip has content I haven't seen before.\n" +
    'Tip: ingest it first (send it without a keyword), then run grounding again.'
  );
}

function _jepaTemporalToText(data) {
  if (data == null) return String(data);
  if (typeof data !== 'object') return String(data);
  if (data.error) return `Error: ${data.error}`;
  const p = data.pyramid || {};
  return [
    '🧠 My memory so far:',
    `  I've recorded ${p.steps != null ? p.steps : '?'} clip(s).`,
    `  I remember ${p.context_size != null ? p.context_size : '?'} distinct visual pattern(s).`,
  ].join('\n');
}

function _jepaHolographicToText(data) {
  if (data == null) return String(data);
  if (typeof data !== 'object') return String(data);
  if (data.error) return `Error: ${data.error}`;
  const rec = data.reconstruction || {};
  const sources = Array.from(
    new Set((rec.sources || []).map((s) => s.label).filter(Boolean))
  ).sort();
  if (!sources.length) return '🧩 No matching memories yet — ingest a few clips first.';
  const lines = ['🧩 Reconstructed from your clip:'];
  lines.push(`  It matches: ${sources.slice(0, 4).join(', ')}${sources.length > 4 ? ' …' : ''}`);
  return lines.join('\n');
}

async function getJSON(path) {
  const resp = await fetch(`${GATEWAY_PROXY}${path}`);
  const data = await resp.json().catch(() => ({}));
  return data;
}

async function postJSON(path, body) {
  const resp = await fetch(`${GATEWAY_PROXY}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await resp.json().catch(() => ({}));
  return data;
}

async function postFile(path, file, fields = {}) {
  const fd = new FormData();
  fd.append('file', file, file.name || 'file');
  Object.keys(fields).forEach((k) => fd.append(k, fields[k]));
  const resp = await fetch(`${GATEWAY_PROXY}${path}`, { method: 'POST', body: fd });
  const data = await resp.json().catch(() => ({}));
  return data;
}

// ── Component ──────────────────────────────────────────────────────────

function NanoLM() {
  const [messages, setMessages] = useState([]);
  const [model, setModel] = useState('NLP');
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [attached, setAttached] = useState(null); // { name, file }
  const [resources, setResources] = useState('');

  const sessionIdRef = useRef(uuid());
  const draftsRef = useRef([]);
  const draftIdxRef = useRef(0);
  const chatRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setMessages([{ role: 'assistant', text: WELCOME_TEXT, time: nowTime() }]);
  }, []);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, busy]);

  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const resp = await fetch(`${GATEWAY_PROXY}/resources`);
        const info = await resp.json().catch(() => ({}));
        if (!alive || !info) return;
        if (info.error && !info.cpu && !info.mem_total) {
          setResources('');
          return;
        }
        const cpu = typeof info.cpu === 'number' ? info.cpu : 0;
        const memBytes = info.mem_bytes || 0;
        const memTotal = info.mem_total || 0;
        setResources(
          `CPU ${cpu.toFixed(1)}% · RAM ${fmtGB(memBytes)} / ${fmtGB(memTotal)} GB`
        );
      } catch (e) {
        if (alive) setResources('');
      }
    };
    refresh();
    const id = setInterval(refresh, 5000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const append = (role, text, images = []) => {
    setMessages((prev) => [...prev, { role, text, time: nowTime(), images }]);
  };

  const newChat = () => {
    sessionIdRef.current = uuid();
    draftsRef.current = [];
    draftIdxRef.current = 0;
    setAttached(null);
    setInput('');
    setMessages([{ role: 'assistant', text: WELCOME_TEXT, time: nowTime() }]);
  };

  const send = async () => {
    const q = input.trim();
    if (!q && !attached) return;
    if (busy) return;

    append('user', q || `📎 ${attached.name}`);
    setInput('');
    if (q) draftsRef.current.push(q);
    draftIdxRef.current = draftsRef.current.length;
    setBusy(true);

    let reply;
    let jepaImages = [];
    try {
      const mode = model;
      const diagnostic =
        mode === 'Diagnose' || (mode === 'NLP' && _isDiagnosticRequest(q));
      const ocrOnly =
        mode === 'OCR' || (mode === 'NLP' && _isNegatedDiagnosticRequest(q));
      const jepa = mode === 'JEPA';

      if (attached) {
        if (jepa) {
          // JEPA mode: route by keyword to the EWM visual-memory endpoints.
          const action = _jepaAction(q);
          let data;
          if (action === 'search') {
            data = await postFile('/jepa/search', attached.file);
            reply = _jepaSearchToText(data);
          } else if (action === 'drn') {
            data = await postFile('/jepa/drn', attached.file);
            reply = _jepaDrnToText(data);
          } else if (action === 'grounding') {
            data = await postFile('/jepa/grounding', attached.file);
            reply = _jepaGroundingToText(data);
          } else if (action === 'holographic') {
            data = await postFile('/jepa/holographic', attached.file);
            reply = _jepaHolographicToText(data);
          } else if (action === 'query') {
            data = await postFile('/jepa/query', attached.file);
            reply = _jepaQueryToText(data);
            const matches = (data && data.matches) || [];
            if (matches.length && matches[0].image) jepaImages = [matches[0].image];
          } else {
            data = await postFile('/jepa/ingest', attached.file, {
              name: attached.name || 'clip',
            });
            reply = _jepaIngestToText(data);
          }
        } else if (diagnostic) {
          const name = (attached.name || '').toLowerCase();
          const isImage = IMAGE_EXTS.some((ext) => name.endsWith(ext));
          const isKnee = KNEE_KEYWORDS.some((k) => q.toLowerCase().includes(k));

          let data;
          if (isImage && isKnee) {
            data = await postFile('/analyze/knee', attached.file, { instruction: q });
          } else if (isImage) {
            data = await postFile('/analyze/image', attached.file, { instruction: q });
          } else {
            data = await postFile('/analyze/document', attached.file, {
              instruction: q,
              modality: 'ECG',
            });
          }

          if (data && data.error && !data.report) {
            reply = `Error: ${data.error}`;
          } else {
            const report = data && data.report ? data.report : data;
            reply = _reportToText(report);
            const ocrText = ((data && data.ocr ? data.ocr.extracted_text : '') || '').trim();
            if (ocrText) reply = `Document contents:\n\n${ocrText}\n\n${reply}`;
          }
        } else if (ocrOnly) {
          const data = await postFile('/ocr/extract', attached.file);
          if (data && data.error) {
            reply = `Error: ${data.error}`;
          } else {
            const text = (data && (data.page_text || data.text)) || '';
            reply = text
              ? `Extracted text:\n\n${text}`
              : "Sorry, I couldn't extract any text from that file.";
          }
        } else {
          // NLP mode + file: OCR the document, then chat about it.
          const data = await postFile('/ocr/extract', attached.file);
          if (data && data.error) {
            reply = `Error: ${data.error}`;
          } else {
            const text = (data && (data.page_text || data.text)) || '';
            if (!text) {
              reply = "Sorry, I couldn't extract any text from that file.";
            } else {
              const r2 = await postJSON('/chat', {
                message: q ? `${q}\n\n${text}` : text,
                session_id: sessionIdRef.current,
              });
              reply = r2.error ? `Error: ${r2.error}` : r2.reply || '';
            }
          }
        }
        setAttached(null);
      } else if (jepa) {
        if (/temporal|pyramid|time lens|memory state/.test(q.toLowerCase())) {
          const data = await getJSON('/jepa/temporal');
          reply = _jepaTemporalToText(data);
        } else {
          reply =
            'JEPA mode is ready — attach an image, GIF, or video and I\'ll encode it ' +
            'into the HLLSet lattice memory. Try: "match", "search", "drn", ' +
            '"grounding", "recall", or "temporal".';
        }
      } else if (diagnostic) {
        if (/\b(document|file|upload|attachment|attach)\b/.test(q.toLowerCase())) {
          reply =
            "Please attach the document or image — I'll extract its contents " +
            'and diagnose any findings.';
        } else {
          const data = await postJSON('/analyze/text', {
            text: q,
            modality: 'ECG',
            instruction: q,
          });
          reply = _reportToText(data);
        }
      } else if (ocrOnly) {
        reply =
          "OCR mode is ready — attach a document or image and I'll extract its text.";
      } else {
        const data = await postJSON('/chat', {
          message: q,
          session_id: sessionIdRef.current,
        });
        reply = data.error ? `Error: ${data.error}` : data.reply || '';
      }
    } catch (e) {
      reply = `Error: ${e && e.message ? e.message : e}`;
    }

    setBusy(false);
    append('assistant', reply, jepaImages);
  };

  const recallPrevious = () => {
    const drafts = draftsRef.current;
    let idx = draftIdxRef.current;
    if (idx > 0) {
      idx -= 1;
      draftIdxRef.current = idx;
      setInput(drafts[idx]);
    }
  };

  const recallNext = () => {
    const drafts = draftsRef.current;
    let idx = draftIdxRef.current;
    if (idx < drafts.length) {
      idx += 1;
      draftIdxRef.current = idx;
      setInput(idx < drafts.length ? drafts[idx] : '');
    }
  };

  const onFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) setAttached({ name: file.name, file });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    } else if (e.key === 'ArrowUp' && !e.shiftKey) {
      e.preventDefault();
      recallPrevious();
    } else if (e.key === 'ArrowDown' && !e.shiftKey) {
      e.preventDefault();
      recallNext();
    }
  };

  return (
    <div className="nanolm-page">
      <SEO {...getSEOData('nanolm')} />
      <header className="nanolm-header">
        <div className="nanolm-header-inner">
          <div className="nanolm-brand">
            <span className="nanolm-logo">🧠</span>
            <div className="nanolm-brand-text">
              <span className="nanolm-brand-title">Guru Emerging World Model</span>
              <span className="nanolm-brand-subtitle">NanoLM AI Assistant</span>
            </div>
          </div>
          <div className="nanolm-header-actions">
            {resources && <span className="nanolm-resources">{resources}</span>}
            <Link
              to="/memory"
              className="nanolm-icon-btn"
              title="Open visual memory"
              aria-label="Open visual memory"
            >
              <MemoryIcon />
            </Link>
            <button className="nanolm-new" onClick={newChat} title="Start a new chat">
              + New chat
            </button>
          </div>
        </div>
      </header>

      {/*
        Scope strip — NanoLM is the umbrella over everything we have working.
        The four modes below are the capabilities behind the portfolio products,
        so this page is the one place to try them; the links carry the reader on
        to the catalogue and to the measured work behind each one.
      */}
      <div className="nanolm-umbrella">
        <div className="nanolm-umbrella-inner">
          <span className="nanolm-umbrella-eyebrow">One front door</span>
          <p className="nanolm-umbrella-lead">
            <strong>NanoLM is the umbrella for all our working applications</strong> — one assistant
            in front of every model we run today. Switch the mode below to use a capability
            directly, or attach a file and NanoLM routes it.
          </p>
          <div className="nanolm-umbrella-modes">
            <span className="nanolm-mode-chip"><strong>NLP</strong> — question answering</span>
            <span className="nanolm-mode-chip"><strong>OCR</strong> — document extraction</span>
            <span className="nanolm-mode-chip"><strong>Diagnose</strong> — report interpretation</span>
            <span className="nanolm-mode-chip"><strong>JEPA</strong> — visual memory</span>
          </div>
          <p className="nanolm-umbrella-links">
            The wider portfolio: <Link to="/products">Products</Link> &middot;{' '}
            <Link to="/applications">Applications by industry</Link> &middot;{' '}
            <Link to="/scenarios">Measured scenarios</Link>
          </p>
        </div>
      </div>

      {/* Research-use notice — the EWM documents require this caveat on all clinical output */}
      <div style={{ maxWidth: '1100px', margin: '12px auto 0', padding: '0 20px' }}>
        <div className="np-disclaimer">{CLINICAL_DISCLAIMER}</div>
      </div>

      <div className="nanolm-chat" ref={chatRef}>
        {messages.map((m, i) => (
          <div key={i} className={`nanolm-msg ${m.role === 'user' ? 'sent' : 'received'}`}>
            {m.role === 'assistant' && <div className="nanolm-msg-name">NanoLM</div>}
            <div className="nanolm-bubble">{m.text}</div>
            {m.images && m.images.length > 0 && (
              <div className="nanolm-msg-images">
                {m.images.map((src, k) => (
                  <img
                    key={k}
                    src={src}
                    alt="Match from visual memory"
                    className="nanolm-msg-image"
                  />
                ))}
              </div>
            )}
            <div className="nanolm-time">{m.time}</div>
          </div>
        ))}
        {busy && (
          <div className="nanolm-msg received">
            <div className="nanolm-msg-name">NanoLM</div>
            <div className="nanolm-bubble nanolm-typing">Thinking…</div>
          </div>
        )}
      </div>

      <div className="nanolm-inputbar">
        <div className="nanolm-inputshell">
          <select
            className="nanolm-model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            disabled={busy}
          >
            {MODEL_MODES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>

          <input ref={fileInputRef} type="file" hidden onChange={onFileChange} />
          <button
            className="nanolm-attach"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            title="Attach a file"
            disabled={busy}
          >
            <PaperclipIcon />
          </button>

          <textarea
            className="nanolm-input"
            placeholder="Ask a question…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            rows={1}
          />

          <button className="nanolm-send" onClick={send} disabled={busy} title="Send">
            <SendIcon />
          </button>
        </div>

        {attached && (
          <div className="nanolm-attached">
            <PaperclipIcon size={14} /> {attached.name}
            <button className="nanolm-attached-remove" onClick={() => setAttached(null)}>
              ✕
            </button>
          </div>
        )}

        <p className="nanolm-hint">
          NanoLM is a platform where NLP, OCR, and any other model can be deployed.
        </p>
        <p className="nanolm-copyright">© 2026 Codentic AI · MIT License</p>
      </div>
    </div>
  );
}

export default NanoLM;
